import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './document-view.html';
import type {
  Document,
  DocumentFile,
  FollowUp,
  FollowUpHistoryEntry,
  SharedUser,
  SecurityLevel,
  FormDefinition,
  FormFieldDefinition,
  Tag,
} from '../services/api.types';
import { SECURITY_LEVELS } from '../services/api.types';
import { apiUrl } from '../services/runtime-config';
import {
  autoTagNamesForFormData as getAutoTagNamesForFormData,
  formValuesHaveChanges,
  formPreviewDetails as getFormPreviewDetails,
  isFormSubmissionFile as isFormSubmissionFileShared,
  parseFormSubmission as parseFormSubmissionText,
} from '../services/form-submission.service';
import type { FormPreviewDetails, FormSubmission } from '../services/form-submission.service';
import {
  addFollowUp as addFollowUpRequest,
  getMe,
  getFollowUpHistory,
  getFollowUpHistoryAvailability,
  removeFollowUp as removeFollowUpRequest,
  shareDocuments,
  unshareDocument,
  updateFollowUp,
  formatFileSize,
  deleteDocument,
  getDocument,
  listForms,
  listTags,
  readDocumentFileText,
  replaceFile,
  updateDocument,
  uploadFile,
} from '../services/api.service';
import { ConfirmDialog } from './confirm-dialog';

type DocumentViewTab = 'details' | 'followUpHistory';

type FollowUpHistoryGroup = {
  followUpId: string;
  latest: FollowUpHistoryEntry;
  previous: FollowUpHistoryEntry[];
};

/** A history entry augmented with flags indicating which fields changed compared to the next-older entry. */
type FollowUpHistoryViewEntry = FollowUpHistoryEntry & {
  isInitial: boolean;
  changedDueDate: boolean;
  changedForUser: boolean;
  changedNote: boolean;
  hasVisibleChanges: boolean;
};

@customElement({ name: 'document-view', template })
export class DocumentView {
  private readonly i18n: I18N = resolve(I18N);
  @bindable public document!: Document;
  @bindable public closed!: () => void;
  @bindable public editRequested!: (document: Document, files?: File[]) => void;
  @bindable public documentUpdated: ((doc: Document) => void) | null = null;
  /** Called after the user confirms deleting this document. */
  @bindable public deleted: (() => void) | null = null;

  public deleting = false;

  public lightboxFile: DocumentFile | null = null;
  public lightboxIndex = 0;
  public linkedDocument: Document | null = null;
  public linkedDocumentLoading = false;
  public linkedDocumentError = '';
  public currentUsername = '';
  public followUpDueDate = '';
  public followUpNote = '';
  public followUpForUser = '';
  public isSavingFollowUp = false;
  /** Id of an already-saved follow-up currently being edited via the modal (null = adding a new one). */
  public editingFollowUpId: string | null = null;
  public followUpError = '';
  /** Whether the add/edit follow-up modal dialog is currently shown. */
  public showFollowUpModal = false;

  // ── Share flyout ────────────────────────────────────────────────────────
  public showShareFlyout = false;
  public shareUsers: SharedUser[] = [];
  public shareSaving = false;
  public shareError = '';

  // ── Form submission ─────────────────────────────────────────────────────
  public showFormChooserModal = false;
  public showFormEntryModal = false;
  public formEntryLoading = false;
  public formEntrySaving = false;
  public formEntryError = '';
  public availableForms: FormDefinition[] = [];
  public formEntryTags: Tag[] = [];
  public selectedFormId = '';
  public formChooserSearch = '';
  public formChooserSuggestionsOpen = false;
  public formChooserHighlightedIndex = -1;
  public formValues: Record<string, unknown> = {};
  public formSideBySideMode = false;
  public formSideBySideFileId: string | null = null;
  private formEntryInitialValues: Record<string, unknown> = {};
  private formEntryCloseConfirmationOpen = false;

  // ── Form file viewer/editor ────────────────────────────────────────────
  public showFormFileModal = false;
  public formFileLoading = false;
  public formFileSaving = false;
  public formFileEditMode = false;
  public formFileError = '';
  public formFile: DocumentFile | null = null;
  public formFileForm: FormDefinition | null = null;
  public formFileTags: Tag[] = [];
  public formFileValues: Record<string, unknown> = {};
  public formFileTagSelections: Record<string, string> = {};
  public formPreviewDetails: Record<string, FormPreviewDetails> = {};
  private formFileOriginalValues: Record<string, unknown> = {};
  private formFileEditInitialValues: Record<string, unknown> = {};
  private formFileCloseConfirmationOpen = false;
  private _formPreviewLoadVersion = 0;
  private _linkedDocumentLoadVersion = 0;
  public camtAnalyticsFile: DocumentFile | null = null;

  public activeTab: DocumentViewTab = 'details';
  public followUpHistoryAvailable = false;
  public followUpHistoryCount = 0;
  public followUpHistoryLoading = false;
  public followUpHistoryLoaded = false;
  public followUpHistoryError = '';
  public followUpHistory: FollowUpHistoryEntry[] = [];
  public expandedFollowUpHistory: Record<string, boolean> = {};

  /** File drag-and-drop from OS into the view modal */
  public isFileDragOver = false;
  private _modalEl: HTMLElement | null = null;
  /** Last measured natural height of the details tab, used to pin the dialog height while the history tab is shown. */
  private _detailsHeightPx: number | null = null;
  public modalElement: HTMLElement | null = null;
  private _keyHandler!: (e: KeyboardEvent) => void;
  private _outsideClickHandler!: (e: MouseEvent) => void;
  private readonly _modalResizeHandler = (): void => {
    if (this.activeTab === 'details') {
      this.scheduleDetailsModalHeightCapture();
    }
  };

  /**
   * Aurelia bindable change callback: if this component instance is reused for a different
   * document (e.g. switching documents while the dialog stays mounted), reset back to the
   * details tab and discard the previous document's follow-up history state.
   */
  public documentChanged(newDoc: Document | undefined, oldDoc: Document | undefined): void {
    if (!oldDoc || !newDoc || newDoc.id === oldDoc.id) return;
    this.closeLinkedDocument();
    void this.closeFormFile(true);
    this.formSideBySideMode = false;
    this.formSideBySideFileId = null;
    this.activeTab = 'details';
    this.followUpHistoryAvailable = false;
    this.followUpHistoryCount = 0;
    this.followUpHistoryLoading = false;
    this.followUpHistoryLoaded = false;
    this.followUpHistoryError = '';
    this.followUpHistory = [];
    this.expandedFollowUpHistory = {};
    this._detailsHeightPx = null;
    this.formPreviewDetails = {};
    this.camtAnalyticsFile = null;
    if (this.modalElement) {
      this.modalElement.style.height = '';
    }
    void this.loadFollowUpHistoryAvailability();
    void this.loadFormPreviewDetails();
  }

  public async attached(): Promise<void> {
    // Always start on the details tab and discard any stale follow-up
    // history state, in case this component instance is being reused for a
    // different document (e.g. switching documents without the `if.bind`
    // host element having been fully torn down in between).
    this.activeTab = 'details';
    this.followUpHistoryAvailable = false;
    this.followUpHistoryCount = 0;
    this.followUpHistoryLoading = false;
    this.followUpHistoryLoaded = false;
    this.followUpHistoryError = '';
    this.followUpHistory = [];
    this.expandedFollowUpHistory = {};
    this._detailsHeightPx = null;
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (this.showShareFlyout) { this.closeShareFlyout(); return; }
        if (this.camtAnalyticsFile) { this.closeCamtAnalytics(); return; }
        if (this.showFormFileModal) { void this.closeFormFile(); return; }
        if (this.showFormChooserModal || this.showFormEntryModal) {
          void this.closeFormEntry();
          return;
        }
        if (this.showFollowUpModal) { this.cancelFollowUpModal(); return; }
        if (this.linkedDocument || this.linkedDocumentLoading || this.linkedDocumentError) {
          this.closeLinkedDocument();
          return;
        }
        if (this.lightboxFile) { this.closeLightbox(); return; }
        this.close();
      }
      if (this.lightboxFile) {
        if (e.key === 'ArrowLeft')  this.lightboxPrev();
        if (e.key === 'ArrowRight') this.lightboxNext();
      }
    };
    document.addEventListener('keydown', this._keyHandler);
    window.addEventListener('resize', this._modalResizeHandler);
    this.scheduleDetailsModalHeightCapture();
    void this.loadFollowUpHistoryAvailability();
    void this.loadFormPreviewDetails();
    try {
      const me = await getMe();
      this.currentUsername = me.username ?? '';
    } catch { /* ignore */ }
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
    window.removeEventListener('resize', this._modalResizeHandler);
    this._removeOutsideClickHandler();
    this.isFileDragOver = false;
    this._modalEl = null;
    this.modalElement = null;
  }

  public close(): void {
    this.closed();
  }

  public async openLinkedDocument(documentId: string): Promise<void> {
    if (!documentId || documentId === this.document.id) return;

    const loadVersion = ++this._linkedDocumentLoadVersion;
    this.linkedDocument = null;
    this.linkedDocumentError = '';
    this.linkedDocumentLoading = true;
    try {
      const linkedDocument = await getDocument(documentId);
      if (loadVersion !== this._linkedDocumentLoadVersion) return;
      this.linkedDocument = linkedDocument;
    } catch (error: unknown) {
      if (loadVersion !== this._linkedDocumentLoadVersion) return;
      this.linkedDocumentError = error instanceof Error
        ? error.message
        : String(error);
    } finally {
      if (loadVersion === this._linkedDocumentLoadVersion) {
        this.linkedDocumentLoading = false;
      }
    }
  }

  public closeLinkedDocument(): void {
    this._linkedDocumentLoadVersion++;
    this.linkedDocument = null;
    this.linkedDocumentLoading = false;
    this.linkedDocumentError = '';
  }

  public onLinkedDocumentUpdated(document: Document): void {
    this.linkedDocument = document;
  }

  public onLinkedDocumentEditRequested(document: Document, files?: File[]): void {
    this.closeLinkedDocument();
    this.editRequested(document, files);
  }

  public edit(files?: File[]): void {
    this.editRequested(this.document, files?.length ? files : undefined);
  }

  public async selectTab(tab: DocumentViewTab): Promise<void> {
    if (tab === this.activeTab) return;
    if (tab === 'followUpHistory' && !this.followUpHistoryAvailable) return;
    if (tab === 'followUpHistory' && this.activeTab === 'details') {
      this.captureDetailsModalHeight();
    }
    this.activeTab = tab;
    if (tab === 'followUpHistory') {
      if (this.modalElement && this._detailsHeightPx) {
        this.modalElement.style.height = `${this._detailsHeightPx}px`;
      }
      await this.loadFollowUpHistory();
    } else {
      if (this.modalElement) {
        this.modalElement.style.height = '';
      }
      this.scheduleDetailsModalHeightCapture();
    }
  }

  public get followUpHistoryGroups(): FollowUpHistoryGroup[] {
    const groups = new Map<string, FollowUpHistoryGroup>();
    for (const entry of this.followUpHistory) {
      const group = groups.get(entry.followUpId);
      if (!group) {
        groups.set(entry.followUpId, {
          followUpId: entry.followUpId,
          latest: entry,
          previous: [],
        });
      } else {
        group.previous.push(entry);
      }
    }

    return [...groups.values()].sort((a, b) =>
      this.historyTimestamp(b.latest.occurredAt) - this.historyTimestamp(a.latest.occurredAt));
  }

  public historyEntriesForGroup(group: FollowUpHistoryGroup): FollowUpHistoryViewEntry[] {
    const entries = [group.latest, ...group.previous];
    return entries.map((entry, index) => {
      const previous = entries[index + 1] ?? null;
      const changedDueDate = !previous || entry.dueDate !== previous.dueDate;
      const changedForUser = !previous || entry.forUserId !== previous.forUserId;
      const changedNote = !previous || (entry.note ?? '') !== (previous.note ?? '');
      return {
        ...entry,
        isInitial: !previous,
        changedDueDate,
        changedForUser,
        changedNote,
        hasVisibleChanges: changedDueDate || changedForUser || (changedNote && !!entry.note),
      };
    });
  }

  public followUpHistoryLabel(group: FollowUpHistoryGroup): string {
    const note = group.latest.note?.trim();
    if (note) return note;
    return `${this.i18n.tr('documents.form.followUpHistory.followUp')} ${group.followUpId.substring(0, 8)}`;
  }

  public followUpHistoryToggleLabel(group: FollowUpHistoryGroup, _expanded?: Record<string, boolean>): string {
    return this.i18n.tr(
      this.isFollowUpHistoryExpanded(group.followUpId)
        ? 'documents.form.followUpHistory.hideEarlier'
        : 'documents.form.followUpHistory.showEarlier',
    );
  }

  // The second parameter is unused by the method body, but passing the
  // `expandedFollowUpHistory` property from the template makes Aurelia's
  // binding observer track it, so the view re-evaluates when it changes.
  public isFollowUpHistoryExpanded(followUpId: string, _expanded?: Record<string, boolean>): boolean {
    return this.expandedFollowUpHistory[followUpId] === true;
  }

  public toggleFollowUpHistory(followUpId: string): void {
    this.expandedFollowUpHistory = {
      ...this.expandedFollowUpHistory,
      [followUpId]: !this.isFollowUpHistoryExpanded(followUpId),
    };
  }

  public historyActionLabel(action: string): string {
    const key = action === 'created'
      ? 'documents.form.followUpHistory.actions.created'
      : action === 'completed'
        ? 'documents.form.followUpHistory.actions.completed'
        : 'documents.form.followUpHistory.actions.updated';
    return this.i18n.tr(key);
  }

  public historyActionClass(action: string): string {
    return action === 'created' || action === 'completed' ? action : 'updated';
  }

  public historyEntryUser(entry: FollowUpHistoryEntry): string {
    return entry.forDisplayName || entry.forUsername || String(entry.forUserId);
  }

  private historyTimestamp(value: string): number {
    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private async loadFollowUpHistoryAvailability(): Promise<void> {
    if (!this.document) return;
    this.followUpHistoryError = '';
    try {
      const availability = await getFollowUpHistoryAvailability(this.document.id);
      this.followUpHistoryCount = availability.count;
      this.followUpHistoryAvailable = availability.hasHistory;
    } catch (err: unknown) {
      this.followUpHistoryAvailable = false;
      this.followUpHistoryError = err instanceof Error
        ? err.message
        : this.i18n.tr('documents.form.followUpHistory.loadFailed');
    }
  }

  private async refreshFollowUpHistory(): Promise<void> {
    this.followUpHistoryLoaded = false;
    this.followUpHistory = [];
    this.expandedFollowUpHistory = {};
    await this.loadFollowUpHistoryAvailability();
    if (this.activeTab === 'followUpHistory') {
      await this.loadFollowUpHistory();
    }
  }

  private async loadFollowUpHistory(): Promise<void> {
    if (!this.document || this.followUpHistoryLoaded || this.followUpHistoryLoading) return;
    this.followUpHistoryLoading = true;
    this.followUpHistoryError = '';
    try {
      this.followUpHistory = await getFollowUpHistory(this.document.id);
      this.followUpHistoryLoaded = true;
    } catch (err: unknown) {
      this.followUpHistoryError = err instanceof Error
        ? err.message
        : this.i18n.tr('documents.form.followUpHistory.loadFailed');
    } finally {
      this.followUpHistoryLoading = false;
    }
  }

  private scheduleDetailsModalHeightCapture(): void {
    if (!this.modalElement || this.activeTab !== 'details') return;
    requestAnimationFrame(() => {
      if (this.activeTab === 'details') {
        this.captureDetailsModalHeight();
      }
    });
  }

  private captureDetailsModalHeight(): void {
    if (!this.modalElement) return;
    // Only records the natural details-tab height; does not pin it onto the
    // element, so the dialog never gets an inline height while details is shown
    // (which previously caused a stray scrollbar on first open).
    const previousInlineHeight = this.modalElement.style.height;
    this.modalElement.style.height = 'auto';
    const measuredHeight = Math.ceil(this.modalElement.getBoundingClientRect().height);
    this.modalElement.style.height = previousInlineHeight;
    if (measuredHeight > 0) {
      this._detailsHeightPx = measuredHeight;
    }
  }

  public async deleteDocument(): Promise<void> {
    if (this.deleting) return;

    const message = this.i18n.tr('documents.delete.confirmOne', { title: this.document.title ?? '' });
    const confirmed = await ConfirmDialog.confirm(message, this.i18n.tr('documents.delete.title'));
    if (!confirmed) return;

    this.deleting = true;
    try {
      await deleteDocument(this.document.id);
      if (this.deleted) this.deleted(); else this.closed();
    } catch (err: unknown) {
      await ConfirmDialog.alert(
        err instanceof Error ? err.message : this.i18n.tr('documents.delete.failed'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.deleting = false;
    }
  }

  public get isOwner(): boolean {
    return !this.document.owner || this.document.owner === this.currentUsername;
  }

  public get assignableFollowUpUsers(): SharedUser[] {
    return this.document.sharedWith ?? [];
  }

  public get followUpNotePlaceholder(): string {
    return this.i18n.tr('documents.form.followUpNote');
  }

  public get followUpAssignToMeLabel(): string {
    return this.i18n.tr('documents.form.assignToMe');
  }

  public get editButtonTitle(): string {
    return this.i18n.tr('common.edit');
  }

  public get removeButtonTitle(): string {
    return this.i18n.tr('common.remove');
  }

  // ── Share flyout ──────────────────────────────────────────────────────────

  public toggleShareFlyout(): void {
    if (this.showShareFlyout) {
      this.closeShareFlyout();
    } else {
      this.openShareFlyout();
    }
  }

  public openShareFlyout(): void {
    this.shareUsers = [...(this.document.sharedWith ?? [])];
    this.shareError = '';
    this.showShareFlyout = true;
    setTimeout(() => {
      this._outsideClickHandler = (e: MouseEvent) => {
        const anchor = (e.target as Element).closest('.share-flyout-anchor');
        const inPortal = (e.target as Element).closest('.user-suggestions');
        if (!anchor && !inPortal) this.closeShareFlyout();
      };
      document.addEventListener('mousedown', this._outsideClickHandler);
    }, 0);
  }

  public closeShareFlyout(): void {
    this.showShareFlyout = false;
    this._removeOutsideClickHandler();
  }

  public async saveShares(): Promise<void> {
    this.shareSaving = true;
    this.shareError = '';
    try {
      const original = this.document.sharedWith ?? [];
      const added = this.shareUsers.filter(u => !original.some(o => o.username === u.username));
      const removed = original.filter(o => !this.shareUsers.some(u => u.username === o.username));

      // Warn if removing a user who has follow-ups on this document
      const followUpsLost = removed.filter(u =>
        (this.document.followUps ?? []).some(fu => fu.forUser?.toLowerCase() === u.username.toLowerCase())
      );
      if (followUpsLost.length > 0) {
        const names = followUpsLost.map(u => u.displayName ?? u.username).join(', ');
        const confirmed = await ConfirmDialog.confirm(
          this.i18n.tr('sharing.removeFollowUpsWarning', { names }),
          this.i18n.tr('sharing.removeConfirmTitle')
        );
        if (!confirmed) {
          this.shareSaving = false;
          return;
        }
      }

      if (added.length > 0) {
        await shareDocuments([this.document.id], added.map(u => u.username));
      }
      for (const u of removed) {
        await unshareDocument(this.document.id, u.username);
      }
      // Update the document in-place so the view reflects new state immediately
      const updated = { ...this.document, sharedWith: [...this.shareUsers] };
      this.document = updated;
      this.documentUpdated?.(updated);
      this.closeShareFlyout();
    } catch (err) {
      this.shareError = err instanceof Error ? err.message : 'Share failed';
    } finally {
      this.shareSaving = false;
    }
  }

  public get selectedForm(): FormDefinition | null {
    return this.availableForms.find(form => form.id === this.selectedFormId) ?? null;
  }

  public get hasFormEntryChanges(): boolean {
    return this.showFormEntryModal &&
      formValuesHaveChanges(this.formValues, this.formEntryInitialValues);
  }

  public get hasFormFileChanges(): boolean {
    if (!this.formFileEditMode || !this.formFileForm) return false;

    const currentValues = { ...this.formFileValues };
    for (const field of this.formFileFields) {
      if (field.dataType === 'tagSelect') {
        currentValues[field.name] = this.formFileTagSelections[field.id] ?? '';
      }
    }
    return formValuesHaveChanges(currentValues, this.formFileEditInitialValues);
  }

  public get selectedFormFields(): FormFieldDefinition[] {
    return this.selectedForm?.fields.slice().sort((a, b) => a.position - b.position) ?? [];
  }

  public get filteredAvailableForms(): FormDefinition[] {
    const search = this.formChooserSearch.trim().toLocaleLowerCase();
    if (!search) return this.availableForms;
    return this.availableForms.filter(form =>
      form.title.toLocaleLowerCase().includes(search));
  }

  public get formSideBySideActive(): boolean {
    return this.formSideBySideMode &&
      (this.showFormEntryModal || this.showFormFileModal);
  }

  public get formSideBySideFile(): DocumentFile | null {
    const files = this.document?.files ?? [];
    return files.find(file => file.id === this.formSideBySideFileId) ?? null;
  }

  public toggleFormSideBySide(): void {
    if (this.formSideBySideMode) {
      this.formSideBySideMode = false;
      if (this.modalElement) {
        this.modalElement.style.height =
          this.activeTab === 'followUpHistory' && this._detailsHeightPx
            ? `${this._detailsHeightPx}px`
            : '';
      }
      return;
    }

    const files = this.document?.files ?? [];
    if (files.length === 0) return;

    const selectedFileExists = this.formSideBySideFileId !== null &&
      files.some(file => file.id === this.formSideBySideFileId);
    if (!selectedFileExists) {
      this.formSideBySideFileId =
        files.find(file => !this.isFormSubmissionFile(file))?.id ?? files[0].id;
    }
    if (this.modalElement) {
      this.modalElement.style.height = '';
    }
    this.formSideBySideMode = true;
  }

  public selectFormSideBySideFile(file: DocumentFile): void {
    if ((this.document?.files ?? []).some(candidate => candidate.id === file.id)) {
      this.formSideBySideFileId = file.id;
    }
  }

  public formSideBySidePrev(): void {
    this.cycleFormSideBySideFile(-1);
  }

  public formSideBySideNext(): void {
    this.cycleFormSideBySideFile(1);
  }

  private cycleFormSideBySideFile(step: number): void {
    const files = this.document?.files ?? [];
    if (files.length <= 1) return;

    const currentIndex = files.findIndex(file => file.id === this.formSideBySideFileId);
    const startIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = (startIndex + step + files.length) % files.length;
    this.formSideBySideFileId = files[nextIndex].id;
  }

  public get activeFormSuggestionId(): string {
    const form = this.filteredAvailableForms[this.formChooserHighlightedIndex];
    return form ? `document-form-suggestion-${form.id}` : '';
  }

  public async openFormEntry(): Promise<void> {
    if (!this.isOwner || this.formEntryLoading || this.formEntrySaving) return;
    this.formSideBySideMode = false;
    this.formSideBySideFileId = null;
    this.showFormChooserModal = true;
    this.showFormEntryModal = false;
    this.formEntryLoading = true;
    this.formEntryError = '';
    this.availableForms = [];
    this.formEntryTags = [];
    this.selectedFormId = '';
    this.formChooserSearch = '';
    this.formChooserSuggestionsOpen = false;
    this.formChooserHighlightedIndex = -1;
    this.formValues = {};
    this.formEntryInitialValues = {};
    try {
      const forms = await listForms();
      this.availableForms = forms.filter(form => form.isActive !== false);
    } catch (error: unknown) {
      this.formEntryError = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.formEntry.loadFailed');
    } finally {
      this.formEntryLoading = false;
    }
  }

  public async closeFormEntry(skipConfirmation = false): Promise<void> {
    if (this.formEntrySaving && !skipConfirmation) return;
    if (!skipConfirmation && this.formEntryCloseConfirmationOpen) return;
    if (!skipConfirmation && this.hasFormEntryChanges) {
      this.formEntryCloseConfirmationOpen = true;
      try {
        const discard = await ConfirmDialog.confirm(
          this.i18n.tr('documents.form.unsavedChanges'),
          this.i18n.tr('documents.form.unsavedChangesTitle'),
        );
        if (!discard) return;
      } finally {
        this.formEntryCloseConfirmationOpen = false;
      }
    }
    this.showFormChooserModal = false;
    this.showFormEntryModal = false;
    this.formSideBySideMode = false;
    this.formSideBySideFileId = null;
    this.formEntryError = '';
    this.selectedFormId = '';
    this.formChooserSearch = '';
    this.formChooserSuggestionsOpen = false;
    this.formChooserHighlightedIndex = -1;
    this.formEntryTags = [];
    this.formValues = {};
    this.formEntryInitialValues = {};
  }

  public openFormChooserSuggestions(): void {
    if (!this.formEntryLoading && this.availableForms.length > 0) {
      this.formChooserSuggestionsOpen = true;
    }
  }

  public toggleFormChooserSuggestions(): void {
    if (!this.formEntryLoading && this.availableForms.length > 0) {
      this.formChooserSuggestionsOpen = !this.formChooserSuggestionsOpen;
      this.formChooserHighlightedIndex = -1;
    }
  }

  public onFormChooserSearchInput(): void {
    this.formChooserSuggestionsOpen = true;
    this.selectedFormId = '';
    this.formChooserHighlightedIndex = -1;
  }

  public onFormChooserKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.formEntryLoading || this.filteredAvailableForms.length === 0) return;

      event.preventDefault();
      this.formChooserSuggestionsOpen = true;
      const forms = this.filteredAvailableForms;
      const currentIndex = this.formChooserHighlightedIndex;
      if (event.key === 'ArrowDown') {
        this.formChooserHighlightedIndex =
          currentIndex < 0 || currentIndex >= forms.length - 1
            ? 0
            : currentIndex + 1;
      } else {
        this.formChooserHighlightedIndex =
          currentIndex <= 0 || currentIndex >= forms.length
            ? forms.length - 1
            : currentIndex - 1;
      }
      return;
    }

    if (event.key === 'Enter' &&
        this.formChooserSuggestionsOpen &&
        this.formChooserHighlightedIndex >= 0) {
      const form = this.filteredAvailableForms[this.formChooserHighlightedIndex];
      if (form) {
        event.preventDefault();
        this.selectFormSuggestion(form);
      }
      return;
    }

    if (event.key === 'Enter' &&
        !this.formChooserSuggestionsOpen &&
        this.selectedForm) {
      event.preventDefault();
      void this.beginSelectedFormEntry();
      return;
    }

    if (event.key === 'Escape' && this.formChooserSuggestionsOpen) {
      event.preventDefault();
      this.formChooserSuggestionsOpen = false;
      this.formChooserHighlightedIndex = -1;
    }
  }

  public selectFormSuggestion(form: FormDefinition): void {
    this.selectedFormId = form.id;
    this.formChooserSearch = form.title;
    this.formChooserSuggestionsOpen = false;
    this.formChooserHighlightedIndex = -1;
    this.formEntryError = '';
  }

  public async beginSelectedFormEntry(): Promise<void> {
    const form = this.selectedForm;
    if (!form || this.formEntryLoading || this.formEntrySaving) return;

    this.selectedFormIdChanged();
    this.formChooserSuggestionsOpen = false;
    this.showFormChooserModal = false;
    this.showFormEntryModal = true;
    this.formEntryLoading = true;
    this.formEntryError = '';
    try {
      this.formEntryTags = await listTags('', false);
    } catch (error: unknown) {
      this.formEntryError = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.formEntry.loadFailed');
    } finally {
      this.formEntryLoading = false;
    }
  }

  public selectedFormIdChanged(): void {
    const form = this.selectedForm;
    this.formValues = {};
    for (const field of form?.fields ?? []) {
      this.formValues[field.id] = field.dataType === 'boolean' ? false : '';
    }
    this.formEntryInitialValues = { ...this.formValues };
    this.formEntryError = '';
  }

  public preventInvalidNumberKey(event: KeyboardEvent): void {
    if (event.isComposing || event.ctrlKey || event.metaKey) return;

    const allowedKeys = new Set([
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'Escape',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ]);
    if (allowedKeys.has(event.key) || /^\d$/.test(event.key)) return;

    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      event.preventDefault();
      return;
    }

    const value = input.value;
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? start;
    const remainingValue = value.slice(0, start) + value.slice(end);
    if ((event.key === '.' || event.key === ',') &&
        !remainingValue.includes('.') && !remainingValue.includes(',')) return;
    if (event.key === '-' && start === 0 && !remainingValue.includes('-')) return;

    event.preventDefault();
  }

  public onFormEntryNumberInput(event: Event, fieldId: string): void {
    const value = this.sanitizeNumberInput(event);
    this.formValues[fieldId] = value;
  }

  public onFormFileNumberInput(event: Event, fieldName: string): void {
    const value = this.sanitizeNumberInput(event);
    this.formFileValues[fieldName] = value;
  }

  public tagsForFormField(field: FormFieldDefinition): Tag[] {
    return this.formEntryTags
      .filter(tag => tag.groupId === field.tagGroupId)
      .sort((a, b) => a.position - b.position);
  }

  private sanitizeNumberInput(event: Event): string {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return '';

    const rawValue = input.value;
    const normalizedInput = rawValue.replace(/,/g, '.');
    const negative = normalizedInput.startsWith('-');
    const normalized = normalizedInput.replace(/[^\d.]/g, '');
    const decimalIndex = normalized.indexOf('.');
    const value = decimalIndex < 0
      ? normalized
      : normalized.slice(0, decimalIndex + 1) +
        normalized.slice(decimalIndex + 1).replace(/\./g, '');
    const sanitized = `${negative ? '-' : ''}${value}`;

    if (input.value !== sanitized) {
      input.value = sanitized;
    }
    return sanitized;
  }

  public async saveFormEntry(): Promise<void> {
    const form = this.selectedForm;
    if (!form || this.formEntrySaving) return;

    const submittedValues: Record<string, string | number | boolean | null> = {};

    for (const field of this.selectedFormFields) {
      const rawValue = this.formValues[field.id];
      if (field.dataType === 'number' && rawValue !== '' && rawValue !== null && rawValue !== undefined) {
        const numberValue = Number(rawValue);
        if (!Number.isFinite(numberValue)) {
          this.formEntryError = this.i18n.tr('documents.form.formEntry.invalidNumber', { name: field.name });
          return;
        }
        submittedValues[field.name] = numberValue;
        continue;
      }

      if (field.dataType === 'tagSelect') {
        const tagId = typeof rawValue === 'string' && rawValue ? rawValue : null;
        const tag = tagId ? this.formEntryTags.find(item => item.id === tagId) : undefined;
        submittedValues[field.name] = tag?.name ?? null;
        continue;
      }

      submittedValues[field.name] = field.dataType === 'boolean'
          ? rawValue === true
          : (typeof rawValue === 'string' && rawValue.length > 0 ? rawValue : null);
    }

    this.formEntrySaving = true;
    this.formEntryError = '';
    try {
      await this.ensureUniqueFormValues(
        form,
        submittedValues,
        undefined,
        'documents.form.formEntry',
      );
      const payload = {
        form: form.title,
        data: submittedValues,
      };
      const fileName = `form-${this.formFileSlug(form.title)}-${this.formFileTimestamp()}.json`;
      const file = new File([JSON.stringify(payload, null, 2)], fileName, {
        type: 'application/json',
      });
      const uploaded = await uploadFile(this.document.id, file);
      if (uploaded.duplicateSkipped) {
        await ConfirmDialog.alert(
          this.i18n.tr('documents.form.duplicateFileSkippedWarning', {
            fileName: uploaded.fileName,
            duplicateFileName: uploaded.duplicateFileName ?? uploaded.fileName,
            documentTitle: uploaded.duplicateDocumentTitle ?? '',
          }),
          this.i18n.tr('documents.form.duplicateFileTitle'),
        );
        return;
      }
      const existingFiles = this.document.files;
      await this.addFormTagsToDocument(this.autoTagNamesForFormData(form, submittedValues));
      const updated = { ...this.document, files: [...existingFiles, uploaded] };
      this.document = updated;
      this.formPreviewDetails = {
        ...this.formPreviewDetails,
        [uploaded.id]: getFormPreviewDetails(form, submittedValues),
      };
      this.documentUpdated?.(updated);
      this.formEntrySaving = false;
      await this.closeFormEntry(true);
    } catch (error: unknown) {
      this.formEntryError = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.formEntry.submitFailed');
    } finally {
      this.formEntrySaving = false;
    }
  }

  private formFileSlug(title: string): string {
    const slug = title
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return slug || 'submission';
  }

  private formFileTimestamp(): string {
    return new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14);
  }

  public get formFileFields(): FormFieldDefinition[] {
    return this.formFileForm?.fields.slice().sort((a, b) => a.position - b.position) ?? [];
  }

  public isFormSubmissionFile(file: DocumentFile): boolean {
    return isFormSubmissionFileShared(file);
  }

  public formPreviewTitle(file: DocumentFile): string {
    return this.formPreviewDetails[file.id]?.formTitle
      ?? this.i18n.tr('documents.form.formFile.title');
  }

  public formPreviewUniqueKey(file: DocumentFile): string {
    return this.formPreviewDetails[file.id]?.uniqueKey ?? '';
  }

  public filePreviewTitle(file: DocumentFile): string {
    return this.isFormSubmissionFile(file) ? this.formPreviewTitle(file) : file.fileName;
  }

  private async loadFormPreviewDetails(): Promise<void> {
    const documentId = this.document?.id;
    const loadVersion = ++this._formPreviewLoadVersion;
    this.formPreviewDetails = {};
    if (!documentId) return;

    const formFiles = (this.document.files ?? []).filter(file =>
      this.isFormSubmissionFile(file));
    if (formFiles.length === 0) return;

    let forms: FormDefinition[] = [];
    try {
      forms = await listForms();
    } catch {
      // The stored submission still contains its title, so preview labels can
      // remain useful even when definitions cannot currently be loaded.
    }
    const formsByTitle = new Map(
      forms.map(form => [form.title.toLocaleLowerCase(), form]),
    );

    await Promise.all(formFiles.map(async file => {
      try {
        const text = await readDocumentFileText(documentId, file.id);
        const submission = parseFormSubmissionText(
          text,
          this.i18n.tr('documents.form.formFile.invalid'),
        );
        const form = formsByTitle.get(submission.title.toLocaleLowerCase());
        const details = form
          ? getFormPreviewDetails(form, submission.data)
          : { formTitle: submission.title, uniqueKey: '' };
        if (loadVersion === this._formPreviewLoadVersion) {
          this.formPreviewDetails = {
            ...this.formPreviewDetails,
            [file.id]: details,
          };
        }
      } catch {
        if (loadVersion === this._formPreviewLoadVersion) {
          this.formPreviewDetails = {
            ...this.formPreviewDetails,
            [file.id]: {
              formTitle: this.i18n.tr('documents.form.formFile.title'),
              uniqueKey: '',
            },
          };
        }
      }
    }));
  }

  public async openFormFile(file: DocumentFile): Promise<void> {
    if (this.formFileLoading || this.formFileSaving) return;

    this.formSideBySideMode = false;
    this.formSideBySideFileId = null;
    this.showFormFileModal = true;
    this.formFileLoading = true;
    this.formFileError = '';
    this.formFile = file;
    this.formFileForm = null;
    this.formFileTags = [];
    this.formFileValues = {};
    this.formFileTagSelections = {};
    this.formFileOriginalValues = {};
    this.formFileEditInitialValues = {};
    this.formFileEditMode = false;

    try {
      const text = await readDocumentFileText(this.document.id, file.id);
      const submission = this.parseFormSubmission(text);
      const forms = await listForms();
      const form = forms.find(candidate =>
        candidate.title.toLowerCase() === submission.title.toLowerCase());
      if (!form) {
        throw new Error(this.i18n.tr('documents.form.formFile.formNotFound', {
          title: submission.title,
        }));
      }

      const values: Record<string, unknown> = {};
      const consumedDataKeys = new Set<string>();
      for (const field of form.fields) {
        const dataKey = Object.keys(submission.data).find(key => key === field.name)
          ?? Object.keys(submission.data).find(key =>
            key.toLocaleLowerCase() === field.name.toLocaleLowerCase());
        if (dataKey !== undefined) {
          values[field.name] = submission.data[dataKey];
          consumedDataKeys.add(dataKey);
        } else {
          values[field.name] = field.dataType === 'boolean' ? false : null;
        }
      }
      for (const [key, value] of Object.entries(submission.data)) {
        if (!consumedDataKeys.has(key) && !Object.prototype.hasOwnProperty.call(values, key)) {
          values[key] = value;
        }
      }
      this.formFileValues = values;
      if (form.fields.some(field => field.dataType === 'tagSelect')) {
        this.formFileTags = await listTags('', false);
      }
      this.formFileForm = form;
      this.formFileOriginalValues = { ...this.formFileValues };
    } catch (error: unknown) {
      this.formFileError = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.formFile.invalid');
    } finally {
      this.formFileLoading = false;
    }
  }

  public async closeFormFile(skipConfirmation = false): Promise<void> {
    if (this.formFileSaving) return;
    if (!skipConfirmation && this.formFileCloseConfirmationOpen) return;
    if (!skipConfirmation && this.hasFormFileChanges) {
      this.formFileCloseConfirmationOpen = true;
      try {
        const discard = await ConfirmDialog.confirm(
          this.i18n.tr('documents.form.unsavedChanges'),
          this.i18n.tr('documents.form.unsavedChangesTitle'),
        );
        if (!discard) return;
      } finally {
        this.formFileCloseConfirmationOpen = false;
      }
    }
    this.showFormFileModal = false;
    this.formSideBySideMode = false;
    this.formSideBySideFileId = null;
    this.formFileLoading = false;
    this.formFileEditMode = false;
    this.formFileError = '';
    this.formFile = null;
    this.formFileForm = null;
    this.formFileTags = [];
    this.formFileValues = {};
    this.formFileTagSelections = {};
    this.formFileOriginalValues = {};
    this.formFileEditInitialValues = {};
  }

  public beginFormFileEdit(): void {
    if (!this.isOwner || !this.formFileForm) return;
    this.formFileOriginalValues = { ...this.formFileValues };
    const editableValues = { ...this.formFileValues };
    const tagSelections: Record<string, string> = {};
    for (const field of this.formFileFields) {
      if (field.dataType !== 'tagSelect') continue;

      const savedValue = editableValues[field.name];
      const matchingTag = this.formFileMatchingTag(field, savedValue);
      const selection = matchingTag?.name ?? '';
      editableValues[field.name] = selection;
      tagSelections[field.id] = selection;
    }
    this.formFileValues = editableValues;
    this.formFileTagSelections = tagSelections;
    this.formFileEditInitialValues = { ...editableValues };
    this.formFileError = '';
    this.formFileEditMode = true;
  }

  public cancelFormFileEdit(): void {
    this.formFileValues = { ...this.formFileOriginalValues };
    this.formFileTagSelections = {};
    this.formFileEditInitialValues = {};
    this.formFileError = '';
    this.formFileEditMode = false;
  }

  public formFileDisplayValue(field: FormFieldDefinition): string {
    const value = this.formFileValues[field.name];
    return value === null || value === undefined ? '' : String(value);
  }

  public formFileDisplaySearchText(field: FormFieldDefinition): string {
    return this.formFileMatchingTag(field, this.formFileValues[field.name])?.searchText ?? '';
  }

  public async saveFormFile(): Promise<void> {
    if (!this.formFileEditMode || !this.formFile || !this.formFileForm ||
        !this.isOwner || this.formFileSaving) return;

    const data: Record<string, unknown> = { ...this.formFileValues };
    for (const field of this.formFileFields) {
      const rawValue = field.dataType === 'tagSelect'
        ? this.formFileTagSelections[field.id]
        : this.formFileValues[field.name];
      if (field.dataType === 'number' && rawValue !== '' && rawValue !== null && rawValue !== undefined) {
        const numberValue = Number(rawValue);
        if (!Number.isFinite(numberValue)) {
          this.formFileError = this.i18n.tr('documents.form.formEntry.invalidNumber', { name: field.name });
          return;
        }
        data[field.name] = numberValue;
      } else if (field.dataType === 'boolean') {
        data[field.name] = rawValue === true;
      } else {
        data[field.name] = typeof rawValue === 'string' && rawValue.length > 0 ? rawValue : null;
      }
    }

    this.formFileSaving = true;
    this.formFileError = '';
    try {
      await this.ensureUniqueFormValues(
        this.formFileForm,
        data,
        this.formFile.id,
        'documents.form.formFile',
      );
      const tagsToRemove = await this.formTagsToRemoveAfterEdit(
        this.formFileForm,
        this.formFileOriginalValues,
        data,
      );
      const payload = {
        form: this.formFileForm.title,
        data,
      };
      const replacement = new File(
        [JSON.stringify(payload, null, 2)],
        this.formFile.fileName,
        { type: 'application/json' },
      );
      const updatedFile = await replaceFile(this.document.id, this.formFile.id, replacement);
      if (updatedFile.duplicateSkipped) {
        await ConfirmDialog.alert(
          this.i18n.tr('documents.form.duplicateFileSkippedWarning', {
            fileName: updatedFile.fileName,
            duplicateFileName: updatedFile.duplicateFileName ?? updatedFile.fileName,
            documentTitle: updatedFile.duplicateDocumentTitle ?? '',
          }),
          this.i18n.tr('documents.form.duplicateFileTitle'),
        );
        return;
      }
      const existingFiles = this.document.files;
      await this.updateFormTags(
        this.autoTagNamesForFormData(this.formFileForm, data),
        tagsToRemove,
      );
      const updated = {
        ...this.document,
        files: existingFiles.map(file => file.id === updatedFile.id ? updatedFile : file),
      };
      this.document = updated;
      this.formPreviewDetails = {
        ...this.formPreviewDetails,
        [updatedFile.id]: getFormPreviewDetails(this.formFileForm, data),
      };
      this.documentUpdated?.(updated);
      if (this.formSideBySideFileId === this.formFile.id) {
        this.formSideBySideFileId = updatedFile.id;
      }
      this.formFile = updatedFile;
      this.formFileValues = data;
      this.formFileTagSelections = {};
      this.formFileOriginalValues = { ...data };
      this.formFileEditInitialValues = {};
      this.formFileEditMode = false;
    } catch (error: unknown) {
      this.formFileError = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.formFile.saveFailed');
    } finally {
      this.formFileSaving = false;
    }
  }

  private parseFormSubmission(text: string) {
    return parseFormSubmissionText(text, this.i18n.tr('documents.form.formFile.invalid'));
  }

  private async ensureUniqueFormValues(
    form: FormDefinition,
    data: Record<string, unknown>,
    excludedFileId: string | undefined,
    translationPrefix: 'documents.form.formEntry' | 'documents.form.formFile',
  ): Promise<void> {
    const uniqueFields = form.fields
      .filter(field => field.uniqueForDocument)
      .sort((a, b) => a.position - b.position);
    if (uniqueFields.length === 0) return;

    const key = this.formUniqueKey(uniqueFields, data);
    if (key === null) return;

    const fieldNames = uniqueFields.map(field => field.name).join(', ');
    for (const file of this.document.files) {
      if (file.id === excludedFileId || !this.isFormSubmissionFile(file)) continue;

      let submission: FormSubmission;
      try {
        const text = await readDocumentFileText(this.document.id, file.id);
        submission = this.parseFormSubmission(text);
      } catch {
        throw new Error(this.i18n.tr(`${translationPrefix}.uniqueCheckFailed`));
      }

      if (submission.title.toLocaleLowerCase() !== form.title.toLocaleLowerCase()) continue;
      if (this.formUniqueKey(uniqueFields, submission.data) !== key) continue;

      throw new Error(this.i18n.tr(`${translationPrefix}.uniqueValueExists`, {
        form: form.title,
        fields: fieldNames,
      }));
    }
  }

  private formUniqueKey(
    fields: FormFieldDefinition[],
    data: Record<string, unknown>,
  ): string | null {
    const values = fields.map(field => this.formValueForField(data, field));
    if (values.some(value => !this.hasUniqueValue(value))) return null;

    return JSON.stringify(values.map(value => this.normalizeUniqueValue(value)));
  }

  private formValueForField(
    data: Record<string, unknown>,
    field: FormFieldDefinition,
  ): unknown {
    const key = Object.keys(data).find(candidate => candidate === field.name)
      ?? Object.keys(data).find(candidate =>
        candidate.toLocaleLowerCase() === field.name.toLocaleLowerCase());
    return key === undefined ? undefined : data[key];
  }

  private hasUniqueValue(value: unknown): boolean {
    return value !== null &&
      value !== undefined &&
      !(typeof value === 'string' && value.trim().length === 0);
  }

  private normalizeUniqueValue(value: unknown): unknown {
    if (typeof value === 'string') return ['string', value.trim()];
    if (typeof value === 'number') return ['number', value];
    if (typeof value === 'boolean') return ['boolean', value];
    return ['value', value];
  }

  public formFileTagsForField(field: FormFieldDefinition): Tag[] {
    const groupId = field.tagGroupId?.toLocaleLowerCase();
    if (!groupId) return [];
    return this.formFileTags
      .filter(tag => tag.groupId?.toLocaleLowerCase() === groupId)
      .sort((a, b) => a.position - b.position);
  }

  private formFileMatchingTag(
    field: FormFieldDefinition,
    savedValue: unknown,
  ): Tag | undefined {
    if (typeof savedValue !== 'string' || savedValue.length === 0) return undefined;
    const availableTags = this.formFileTagsForField(field);
    return availableTags.find(tag => tag.name === savedValue)
      ?? availableTags.find(tag =>
        tag.name.toLocaleLowerCase() === savedValue.toLocaleLowerCase());
  }

  private autoTagNamesForFormData(
    form: FormDefinition,
    data: Record<string, unknown>,
  ): string[] {
    return getAutoTagNamesForFormData(form, data);
  }

  private async formTagsToRemoveAfterEdit(
    form: FormDefinition,
    previousData: Record<string, unknown>,
    currentData: Record<string, unknown>,
  ): Promise<string[]> {
    const currentTagKeys = new Set(
      this.autoTagNamesForFormData(form, currentData)
        .map(tagName => tagName.toLocaleLowerCase()),
    );
    const previousTags = new Map(
      this.autoTagNamesForFormData(form, previousData)
        .map(tagName => [tagName.toLocaleLowerCase(), tagName]),
    );
    const removedTags = [...previousTags]
      .filter(([tagKey]) => !currentTagKeys.has(tagKey));
    if (removedTags.length === 0) return [];

    const otherFormFiles = this.document.files.filter(file =>
      file.id !== this.formFile!.id && this.isFormSubmissionFile(file));
    const remainingTagKeys = new Set(currentTagKeys);
    if (otherFormFiles.length > 0) {
      let forms: FormDefinition[];
      try {
        forms = await listForms();
      } catch {
        throw new Error(this.i18n.tr('documents.form.formFile.tagChangeCheckFailed'));
      }

      const formsByTitle = new Map(
        forms.map(candidate => [candidate.title.toLocaleLowerCase(), candidate]),
      );
      for (const file of otherFormFiles) {
        let submission: FormSubmission;
        try {
          const text = await readDocumentFileText(this.document.id, file.id);
          submission = this.parseFormSubmission(text);
        } catch {
          throw new Error(this.i18n.tr('documents.form.formFile.tagChangeCheckFailed'));
        }

        const otherForm = formsByTitle.get(submission.title.toLocaleLowerCase());
        if (!otherForm) return [];
        for (const tagName of this.autoTagNamesForFormData(otherForm, submission.data)) {
          remainingTagKeys.add(tagName.toLocaleLowerCase());
        }
      }
    }

    const tagsToRemove: string[] = [];
    for (const [tagKey, tagName] of removedTags) {
      if (remainingTagKeys.has(tagKey)) continue;

      const selectedTag = (this.document.tags ?? []).find(tag =>
        tag.name.toLocaleLowerCase() === tagKey);
      if (!selectedTag) continue;

      const confirmed = await ConfirmDialog.confirm(
        this.i18n.tr('documents.form.formFile.removeAutoTagConfirm', { tag: tagName }),
        this.i18n.tr('documents.form.formFile.removeAutoTagTitle'),
      );
      if (confirmed) tagsToRemove.push(selectedTag.name);
    }
    return tagsToRemove;
  }

  private async addFormTagsToDocument(tagNames: string[]): Promise<void> {
    await this.updateFormTags(tagNames, []);
  }

  private async updateFormTags(
    tagNamesToAdd: string[],
    tagNamesToRemove: string[],
  ): Promise<void> {
    const currentTags = this.document.tags ?? [];
    const removalKeys = new Set(
      tagNamesToRemove.map(tagName => tagName.toLocaleLowerCase()),
    );
    const names = currentTags
      .filter(tag => !removalKeys.has(tag.name.toLocaleLowerCase()))
      .map(tag => tag.name);
    const seen = new Set(names.map(name => name.toLocaleLowerCase()));
    for (const tagName of tagNamesToAdd) {
      const key = tagName.toLocaleLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(tagName);
    }
    const unchanged = names.length === currentTags.length &&
      names.every((name, index) => name === currentTags[index].name);
    if (unchanged) return;

    const updated = await updateDocument(this.document.id, {
      title: this.document.title,
      description: this.document.description,
      documentDate: this.document.documentDate,
      tagNames: names,
      securityLevel: this.document.securityLevel,
    });
    this.document = updated;
    this.documentUpdated?.(updated);
  }

  private _removeOutsideClickHandler(): void {
    if (this._outsideClickHandler) {
      document.removeEventListener('mousedown', this._outsideClickHandler);
    }
  }

  // ── OS drag-and-drop into view modal ─────────────────────────────────────

  public onDragEnter(e: DragEvent): void {
    if (this.lightboxFile) return;
    if (e.dataTransfer?.types.includes('Files')) {
      this.isFileDragOver = true;
      if (!this._modalEl) this._modalEl = e.currentTarget as HTMLElement;
    }
  }

  public onDragLeave(e: DragEvent): void {
    if (this.lightboxFile) return;
    const related = e.relatedTarget as Node | null;
    if (related && this._modalEl?.contains(related)) return;
    this.isFileDragOver = false;
  }

  public onDragOver(e: DragEvent): void {
    if (this.lightboxFile) return;
    if (e.dataTransfer?.types.includes('Files') || this.isFileDragOver) {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    }
  }

  public onDrop(e: DragEvent): void {
    if (this.lightboxFile) return;
    e.preventDefault();
    this.isFileDragOver = false;
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    if (files.length > 0) this.edit(files);
  }

  public previewUrl(docId: string, fileId: string, uploadedAt?: string): string {
    const version = uploadedAt ? `?v=${encodeURIComponent(uploadedAt)}` : '';
    return apiUrl(`/documents/${docId}/files/${fileId}/preview${version}`);
  }

  public downloadUrl(docId: string, fileId: string): string {
    return apiUrl(`/documents/${docId}/files/${fileId}/download`);
  }

  public openFileInNewTab(docId: string, fileId: string): void {
    window.open(this.downloadUrl(docId, fileId), '_blank', 'noopener,noreferrer');
  }

  public isCamtFile(file: DocumentFile): boolean {
    return file.contentClassification?.toLocaleLowerCase().startsWith('camt:') ?? false;
  }

  public openCamtAnalytics(file: DocumentFile): void {
    if (this.isCamtFile(file)) this.camtAnalyticsFile = file;
  }

  public closeCamtAnalytics(): void {
    this.camtAnalyticsFile = null;
  }

  public openExtractedTextInNewWindow(docId: string, fileId: string): void {
    const textUrl = apiUrl(`/documents/${docId}/files/${fileId}/text`);
    window.open(textUrl, '_blank', 'noopener,noreferrer,width=1000,height=800,resizable=yes,scrollbars=yes');
  }

  public fileSizeLabel(file: DocumentFile): string {
    return formatFileSize(file.fileSizeBytes);
  }

  public get totalFileSizeLabel(): string {
    const files = this.document?.files ?? [];
    const knownSizes = files.filter(f => f.fileSizeBytes != null);
    if (knownSizes.length === 0) return '';
    const total = knownSizes.reduce((sum, f) => sum + (f.fileSizeBytes ?? 0), 0);
    return formatFileSize(total);
  }

  public openLightbox(file: DocumentFile, index: number): void {
    this.lightboxFile = file;
    this.lightboxIndex = index;
  }

  public closeLightbox(): void {
    this.lightboxFile = null;
  }

  public lightboxPrev(): void {
    const files = this.document.files;
    if (files.length <= 1) return;
    this.lightboxIndex = (this.lightboxIndex - 1 + files.length) % files.length;
    this.lightboxFile = files[this.lightboxIndex];
  }

  public lightboxNext(): void {
    const files = this.document.files;
    if (files.length <= 1) return;
    this.lightboxIndex = (this.lightboxIndex + 1) % files.length;
    this.lightboxFile = files[this.lightboxIndex];
  }

  public isImage(file: DocumentFile): boolean {
    const ct = (file.contentType ?? '').toLowerCase();
    const ext = file.fileName.toLowerCase();
    return ct.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|svg|tiff?)$/i.test(ext);
  }

  public isPdf(file: DocumentFile): boolean {
    const ct = (file.contentType ?? '').toLowerCase();
    const ext = file.fileName.toLowerCase();
    return ct === 'application/pdf' || ext.endsWith('.pdf');
  }

  public hasFormPreview(file: DocumentFile | null): boolean {
    return file !== null && file.hasPreview && this.isFormSubmissionFile(file);
  }

  public securityLevelLabel(level: SecurityLevel): string {
    const sl = SECURITY_LEVELS.find(s => s.value === level);
    return sl ? this.i18n.tr(sl.key) : String(level);
  }

  public securityLevelClass(level: SecurityLevel): string {
    switch (level) {
      case 0: return 'public';
      case 1: return 'internal';
      case 2: return 'confidential';
      case 3: return 'restricted';
      default: return 'internal';
    }
  }

  public displayName(username?: string | null, name?: string | null): string {
    return name || username || '';
  }

  public fuTooltip(fu: FollowUp): string {
    const parts: string[] = [];
    if (fu.createdAt) parts.push(`${this.i18n.tr('documents.form.createdAt')}: ${this.formatDate(fu.createdAt)}`);
    if (fu.creatorUsername) parts.push(`${this.i18n.tr('documents.form.createdBy')}: ${this.fuCreatorName(fu)}`);
    return parts.join('\n');
  }

  public fuIsForOther(fu: FollowUp): boolean {
    return !!fu.forUser && !this.isCurrentUser(fu.forUser);
  }

  public fuShowCreator(fu: FollowUp): boolean {
    return !!fu.creatorUsername && fu.creatorUsername.toLowerCase() !== (fu.forUser ?? '').toLowerCase();
  }

  public fuForName(fu: FollowUp): string {
    return this.displayName(fu.forUser, fu.forUserDisplayName);
  }

  public fuCreatorName(fu: FollowUp): string {
    return this.displayName(fu.creatorUsername, fu.creatorDisplayName);
  }

  public fuCanEdit(fu: FollowUp): boolean {
    return this.isOwner || this.isCurrentUser(fu.forUser ?? '');
  }

  public formatDate(iso: string): string {
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  }

  public get isEditingFollowUp(): boolean {
    return this.editingFollowUpId !== null;
  }

  public get followUpModalTitle(): string {
    return this.isEditingFollowUp
      ? this.i18n.tr('documents.form.editFollowUp')
      : this.i18n.tr('documents.form.addFollowUp');
  }

  public startEditFollowUp(fu: FollowUp): void {
    this.editingFollowUpId = fu.id;
    this.followUpDueDate = fu.dueDate.substring(0, 10);
    this.followUpNote = fu.note ?? '';
    this.followUpForUser = fu.forUser ?? this.currentUsername;
    this.followUpError = '';
    this.showFollowUpModal = true;
  }

  public openAddFollowUp(): void {
    this.editingFollowUpId = null;
    this.followUpDueDate = '';
    this.followUpNote = '';
    this.followUpForUser = '';
    this.followUpError = '';
    this.showFollowUpModal = true;
  }

  public cancelFollowUpModal(): void {
    this.editingFollowUpId = null;
    this.followUpDueDate = '';
    this.followUpNote = '';
    this.followUpForUser = '';
    this.followUpError = '';
    this.showFollowUpModal = false;
  }

  public async confirmFollowUp(): Promise<void> {
    if (!this.followUpDueDate || this.isSavingFollowUp) return;

    this.isSavingFollowUp = true;
    this.followUpError = '';
    try {
      if (this.editingFollowUpId) {
        const updated = await updateFollowUp(this.document.id, this.editingFollowUpId, {
          dueDate: new Date(this.followUpDueDate).toISOString(),
          note: this.followUpNote.trim() || undefined,
          forUsername: this.isOwner ? (this.followUpForUser || this.currentUsername || undefined) : undefined,
        });
        this.applyFollowUps((this.document.followUps ?? []).map(item => item.id === updated.id ? updated : item));
      } else {
        const created = await addFollowUpRequest(
          this.document.id,
          new Date(this.followUpDueDate).toISOString(),
          this.followUpNote.trim() || undefined,
          this.isOwner ? (this.followUpForUser || undefined) : undefined,
        );
        this.applyFollowUps([...(this.document.followUps ?? []), created]);
      }
      await this.refreshFollowUpHistory();
      this.cancelFollowUpModal();
    } catch (err) {
      this.followUpError = err instanceof Error ? err.message : this.i18n.tr('documents.form.saveFailed');
    } finally {
      this.isSavingFollowUp = false;
    }
  }

  public async removeFollowUp(fu: FollowUp): Promise<void> {
    if (this.isSavingFollowUp) return;

    this.isSavingFollowUp = true;
    this.followUpError = '';
    try {
      await removeFollowUpRequest(this.document.id, fu.id);
      this.applyFollowUps((this.document.followUps ?? []).filter(item => item.id !== fu.id));
      await this.refreshFollowUpHistory();
      if (this.editingFollowUpId === fu.id) {
        this.cancelFollowUpModal();
      }
    } catch (err) {
      this.followUpError = err instanceof Error ? err.message : this.i18n.tr('documents.form.saveFailed');
    } finally {
      this.isSavingFollowUp = false;
    }
  }

  private applyFollowUps(followUps: FollowUp[]): void {
    const updated = { ...this.document, followUps };
    this.document = updated;
    this.documentUpdated?.(updated);
  }

  private isCurrentUser(username?: string | null): boolean {
    const currentUsername = this.currentUsername || this.document.owner || '';
    return !!username && !!currentUsername && username.toLowerCase() === currentUsername.toLowerCase();
  }
}
